// @ts-nocheck
/**
 * Unified Teacher Calendar Service
 * Connects all major platform modules: Assessments, Data Points, Assignments, Events, and Teacher Notes.
 */

import { API_URL, authFetch } from './api';
import { fetchAssignments } from './assignments';
import { fetchTeacherNotes } from './teacherNotes';

export const CALENDAR_CATEGORIES = {
  assessment: {
    key: 'assessment',
    label: 'Assessments & Tests',
    badge: 'Assessment',
    color: '#8b5cf6', // Purple
    bgLight: '#f5f3ff',
    border: '#ddd6fe',
    icon: '🎯',
    description: 'Scheduled assessments and evaluation deadlines',
  },
  data_point: {
    key: 'data_point',
    label: 'Student Data Points',
    badge: 'Data Point',
    color: '#10b981', // Emerald green
    bgLight: '#ecfdf5',
    border: '#a7f3d0',
    icon: '📊',
    description: 'Student progression scoring & data entry dates',
  },
  assignment: {
    key: 'assignment',
    label: 'Assignments & Tasks',
    badge: 'Assignment',
    color: '#2563eb', // Blue
    bgLight: '#eff6ff',
    border: '#bfdbfe',
    icon: '📝',
    description: 'Assignment due dates, scheduled classwork & quizzes',
  },
  event: {
    key: 'event',
    label: 'School & Class Events',
    badge: 'Event',
    color: '#06b6d4', // Teal/Cyan
    bgLight: '#ecfeff',
    border: '#a5f3fc',
    icon: '🗓️',
    description: 'School assemblies, conferences & academic events',
  },
  note: {
    key: 'note',
    label: 'Teacher Notes',
    badge: 'Note',
    color: '#f59e0b', // Amber/Orange
    bgLight: '#fffbeb',
    border: '#fde68a',
    icon: '📌',
    description: 'Classroom observations, reflections & student notes',
  },
};

export const CALENDAR_CATEGORIES_LIST = Object.values(CALENDAR_CATEGORIES);

/**
 * Fetch unified calendar items combining all 5 modules
 */
export async function fetchUnifiedCalendarEvents({
  startDate = null,
  endDate = null,
  teacherId = null,
  schoolId = null,
  teacherClasses = [],
  activeCategories = ['assessment', 'data_point', 'assignment', 'note', 'event'],
} = {}) {
  const items = [];

  // 1. Fetch Assignments & Assessments
  try {
    const assignments = await fetchAssignments({ teacherId, teacherClasses });
    assignments.forEach((a) => {
      // Due Date item
      if (a.dueDate) {
        const isAssessment = a.type === 'Test' || a.type === 'Quiz' || a.assessmentDate === a.dueDate;
        items.push({
          id: `cal-asgn-due-${a.id}`,
          title: `${a.title} (${a.type || 'Assignment'} Due)`,
          date: a.dueDate,
          category: isAssessment ? 'assessment' : 'assignment',
          className: a.className,
          subject: a.subject,
          status: a.status,
          assignmentId: a.id,
          standardsCount: (a.standards || []).length,
          studentsCount: (a.students || []).length,
          subtitle: `${a.className || 'Class'} • ${a.subject}`,
          description: a.instructions || 'Standards-aligned assignment due date.',
          actionLink: `/teacher/assignments/${a.id}/grade`,
          actionLabel: isAssessment ? 'Grade Assessment' : 'Open Grading Grid',
          rawItem: a,
        });
      }

      // Assessment Date item if distinct
      if (a.assessmentDate && a.assessmentDate !== a.dueDate) {
        items.push({
          id: `cal-asgn-eval-${a.id}`,
          title: `${a.title} (Assessment Window)`,
          date: a.assessmentDate,
          category: 'assessment',
          className: a.className,
          subject: a.subject,
          status: a.status,
          assignmentId: a.id,
          standardsCount: (a.standards || []).length,
          studentsCount: (a.students || []).length,
          subtitle: `${a.className || 'Class'} • ${a.subject}`,
          description: a.instructions || 'Scheduled assessment window for priority standards.',
          actionLink: `/teacher/assignments/${a.id}/grade`,
          actionLabel: 'Grade Assessment',
          rawItem: a,
        });
      }
    });
  } catch (err: any) {
    console.warn('[Calendar] Could not fetch assignments for calendar:', err);
  }

  // 2. Fetch Live Data Points from backend
  try {
    const res = await authFetch(`${API_URL}/api/data-points/report`);
    if (res?.ok) {
      const dataPoints = await res.json();
      if (Array.isArray(dataPoints) && dataPoints.length > 0) {
        // Group data points by date & student/class to keep calendar clean
        const byDate = new Map();
        dataPoints.forEach((dp) => {
          if (!dp.date) return;
          const key = `${dp.date}_${dp.student_id || dp.student_name}`;
          if (!byDate.has(key)) {
            byDate.set(key, {
              id: `cal-dp-${dp.id}`,
              title: `${dp.student_name || 'Student'}: ${dp.standard_code || 'Standard'} (${dp.score ? `${dp.score}/4` : 'Evaluated'})`,
              date: dp.date,
              category: 'data_point',
              studentName: dp.student_name,
              studentId: dp.student_id,
              standardCode: dp.standard_code,
              score: dp.score,
              color: dp.color,
              notes: dp.notes,
              subject: dp.subject_name || 'General',
              subtitle: `Recorded for ${dp.student_name || 'Student'} • Score: ${dp.score || '—'}/4`,
              description: dp.notes ? `Observation: ${dp.notes}` : `Data point recorded for standard ${dp.standard_code}.`,
              actionLink: '/teacher/data-points',
              actionLabel: 'View Data Points Report',
            });
          }
        });
        items.push(...Array.from(byDate.values()).slice(0, 40));
      }
    }
  } catch (err: any) {
    console.warn('[Calendar] Could not fetch real data points for calendar:', err);
  }

  // 3. Fetch Teacher Notes
  try {
    const notes = await fetchTeacherNotes({ teacherId, teacherClasses });
    notes.forEach((n) => {
      const isReminder = Boolean(n.hasReminder || n.category === 'Reminder');
      const eventDate = (isReminder && n.reminderDate) ? n.reminderDate : n.date;
      if (eventDate) {
        items.push({
          id: `cal-note-${n.id}`,
          title: isReminder ? `⏰ Reminder: ${n.title}` : `Note: ${n.title}`,
          date: eventDate,
          category: 'note',
          subject: n.subject,
          className: n.className,
          noteCategory: n.category,
          studentName: n.studentName,
          noteId: n.id,
          content: n.content,
          hasReminder: isReminder,
          reminderTime: n.reminderTime,
          subtitle: `${n.subject || 'General'} • ${n.studentName || n.className || 'General Note'}${isReminder ? ` (⏰ ${n.reminderTime || '9:00 AM'})` : ''}`,
          description: n.content,
          actionLink: '/teacher/notes',
          actionLabel: 'Open in Teacher Notes',
          actionState: { selectedDate: eventDate },
        });
      }
    });
  } catch (err: any) {
    console.warn('[Calendar] Could not fetch teacher notes for calendar:', err);
  }

  // School events come from the same backend source used by SIS event imports.
  if (schoolId != null) {
    const response = await authFetch(`${API_URL}/api/v1/calendar/events?school_id=${encodeURIComponent(schoolId)}`);
    if (!response?.ok) throw new Error('Could not load school calendar events.');
    const data = await response.json();
    if (!Array.isArray(data.items)) throw new Error('Invalid school calendar event response.');
    data.items.forEach(event => {
      items.push({
        id: `cal-event-${event.event_id}`,
        title: event.title,
        date: event.event_date,
        category: 'event',
        subtitle: event.event_type || 'School Event',
        description: event.description || '',
        rawItem: event,
      });
    });
  }

  // Filter by active categories
  let filtered = items;
  if (Array.isArray(activeCategories) && activeCategories.length > 0) {
    filtered = filtered.filter((item) => activeCategories.includes(item.category));
  }

  // Filter by date range if provided
  if (startDate && endDate) {
    filtered = filtered.filter((item) => {
      if (!item.date) return false;
      return item.date >= startDate && item.date <= endDate;
    });
  }

  // Sort ascending by date
  filtered.sort((a, b) => (a.date || '').localeCompare(b.date || ''));

  return filtered;
}

// Alias for backwards compatibility
export const fetchUnifiedCalendarItems = fetchUnifiedCalendarEvents;
